import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Menu, Typography, Dropdown, Button, Spin } from "antd";
import { journalsQueries } from "@/entities/journal";
import { $journalApi } from "@/entities/journal/fetch/fetch-client";
import { RepresentativeModalOrgs } from "@/features/representation-orgs-modal";
import { EditIcon, BasketIcon, MoreOneIcon } from "@/shared/assets/svg/icons";
import { Can } from "@/shared/lib/casl";
import { useCoordinator, useDisclosure } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { fallbackString } from "@/shared/utils";
import { useTitleAmendmentMode } from "../../../../model";
import { RepresentativeWrapper } from "../../../representative-wrapper";
import { RepresentativesSignatureWrapper } from "../../../representatives-signature-wrapper";

export function BuildingContractorControls() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [key, setKey] = useState(0);
  const { titleId } = useCoordinator();
  const { data: titleData, isFetching: isTitleFetching } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const titleStatus = titleData?.mainJournal?.titleJournal?.status === "NEW";
  const { isNewAmendment } = useTitleAmendmentMode();
  const canAddData = titleStatus || isNewAmendment;

  const buildingContractor =
    titleData?.mainJournal?.titleJournal?.buildingContractor;
  const constructionControls = buildingContractor?.constructionControls || [];
  const hasConstructionControls =
    buildingContractor?.projectParticipantId && constructionControls.length > 0;

  const handleClose = () => {
    onClose();
    setKey((prev) => prev + 1);
  };

  const { mutate: removeParticipant, isPending } = $journalApi.useMutation(
    "delete",
    "/api/v1/journals/title/{titleId}/constructionControl",
    {
      body: (data: any) => data,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: journalsQueries.getTitleJournal({ titleId }).queryKey,
        });
      },
    },
  );

  const handleDelete = (id: string) => {
    removeParticipant({
      params: {
        path: {
          titleId,
        },
      },
      body: {
        participants: {
          buildingContractor: {
            projectParticipantId: buildingContractor?.projectParticipantId,
            constructionControls: [{ constructionControlId: id }],
          },
        },
      },
    });
  };

  const menu = (id: string) => (
    <Menu>
      <Menu.Item key="edit" icon={<EditIcon />} onClick={onOpen}>
        Редактировать
      </Menu.Item>
      <Menu.Item
        key="delete"
        icon={<BasketIcon />}
        onClick={() => handleDelete(id)}
      >
        Удалить
      </Menu.Item>
    </Menu>
  );

  return (
    <Spin spinning={isPending || isTitleFetching}>
      <div>
        {canAddData ? (
          <div className="journal-title-block-header journal-title-block-header--spaced">
            <div className="journal-title-section-heading">
              Уполномоченный представитель лица, осуществляющего строительство,
              по вопросам строительного контроля
            </div>

            <Can I="edit" a="title_journals">
              <Button
                type={constructionControls?.length > 0 ? "default" : "primary"}
                onClick={onOpen}
                disabled={
                  !buildingContractor?.projectParticipantId ||
                  isTitleFetching ||
                  isPending
                }
                style={{
                  // @ts-expect-error backgroundColor может быть boolean из-за short-circuit
                  backgroundColor:
                    constructionControls?.length > 0 &&
                    "rgba(234, 238, 248, 1)",
                }}
              >
                Добавить сотрудника
              </Button>
            </Can>
            <RepresentativeModalOrgs
              key={key}
              organization={{
                representativeType: "buildingContractor",
                isConstructionControl: true,
                companyId: buildingContractor?.company?.companyId ?? "",
                projectParticipantId:
                  buildingContractor?.projectParticipantId ?? "",
                userCompaniesHistoryId:
                  buildingContractor?.company?.companyHistoryId ?? "",
              }}
              isOpen={isOpen}
              handleClose={handleClose}
              isControls
              // @ts-expect-error existingRepresentatives не совпадает с openapi типом
              existingRepresentatives={constructionControls}
              enableMultiAdd
              isAddFromOldService
            />
          </div>
        ) : null}
        {hasConstructionControls
          ? constructionControls.map((control, index) => (
              <RepresentativesSignatureWrapper
                representative={control}
                representativeType="buildingContractor"
                isConstructionControl
              >
                <div
                  key={control.constructionControlId || index}
                  className="journal-title-rep-card"
                >
                  <div className="journal-title-rep-card__header">
                    <Typography.Title
                      level={5}
                      className="journal-title-rep-card__name"
                    >
                      {fallbackString(
                        `${control.user?.surname || ""} ${control.user?.firstName || ""} ${control.user?.middleName || ""}`,
                      )}
                    </Typography.Title>
                    {control.constructionControlId &&
                      titleStatus &&
                      !isNewAmendment && (
                        <Can I="edit" a="title_journals">
                          <Dropdown
                            overlay={menu(control?.constructionControlId)}
                            trigger={["click"]}
                          >
                            <div className="journal-title-menu-trigger">
                              <MoreOneIcon width={24} height={24} />
                            </div>
                          </Dropdown>
                        </Can>
                      )}
                  </div>

                  <RepresentativeWrapper rep={control} />
                </div>
              </RepresentativesSignatureWrapper>
            ))
          : null}
      </div>
    </Spin>
  );
}
